import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  MapPin, 
  Target as TargetIcon,
  Handshake,
  TrendingUp,
  ScrollText,
  Lock
} from 'lucide-react';

const TEAM_IMAGE = "https://images.unsplash.com/photo-1517502884422-41eaead166d4?w=1200&q=85&auto=format";

// Toggle flags for sections hidden per feedback (may be re-enabled later)
const SHOW_LEADERSHIP_TEAM = false;
const SHOW_RECOGNITION = false;

export const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="section-padding bg-[#0A2540]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">About Us</span>
              <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6 leading-tight">
                Empowering Canadian Businesses Since 2021
              </h1>
              <p className="text-white/85 text-lg md:text-xl mb-8 leading-relaxed">
                Blue Ribbon Technologies was founded with a simple mission: make practical, affordable digital solutions accessible to Canadian owner-led businesses. We believe every business deserves the clarity, tools, and confidence to compete in a digital-first economy.
              </p>
              <div className="flex flex-wrap items-center gap-8">
                <div>
                  <div className="text-3xl font-bold text-white">70+</div>
                  <div className="text-sm text-white/70">Businesses Served</div>
                </div>
                <div className="w-px h-12 bg-white/20 hidden sm:block" />
                <div>
                  <div className="text-3xl font-bold text-white">37%</div>
                  <div className="text-sm text-white/70">Avg Cost Reduction</div>
                </div>
                <div className="w-px h-12 bg-white/20 hidden sm:block" />
                <div>
                  <div className="text-3xl font-bold text-white">15+ hrs</div>
                  <div className="text-sm text-white/70">Saved per Week</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src={TEAM_IMAGE} 
                alt="Blue Ribbon Technologies Team" 
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#2563EB]" />
                  <span className="font-medium text-[#0A2540]">Vancouver, BC</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story, Microsoft / Amazon / Shopify name-drops removed per feedback */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-[#0A2540] mb-8 text-center">Our Story</h2>
            <div className="prose prose-lg text-slate-700 space-y-6">
              <p className="text-lg leading-relaxed">
                In 2021, our founders saw a persistent gap in the Canadian market. While large enterprises had access to modern AI and automation, owner-led SMBs were left behind, priced out of the digital transformation reshaping every industry.
              </p>
              <p className="text-lg leading-relaxed">
                We started Blue Ribbon Technologies with a bold vision: democratize access to practical enterprise-grade technology. Our core team is made up of <strong>techno-functional experts</strong> with vast experience identifying and aligning digital solutions to real industry and business needs, the kind of hands-on operators who have shipped, scaled, and supported systems in production.
              </p>
              <p className="text-lg leading-relaxed">
                Today, we've helped Canadian SMBs across manufacturing, professional services, retail, and B2B transform their operations, generating measurable savings and efficiency gains. And we're still just getting started. As AI matures, we're committed to ensuring every business, regardless of size, can harness it with clarity and control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Promise, replaces Values, using 5 Trust & Transparency points */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <span className="text-sm font-semibold text-[#2563EB] uppercase tracking-wider">Our Promise</span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold text-[#0A2540] mt-4 mb-6 leading-tight">
              How We Show Up for Every Client
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Consulting has a trust problem. We fix it by being explicit about what we do, why we do it, and how we prove it worked.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: TargetIcon,
                title: "Clear scope. Clear outcomes.",
                description: "You know what is being built, why it matters, and how success will be measured."
              },
              {
                icon: Handshake,
                title: "Practical implementation.",
                description: "We prioritize changes that are realistic for owner-led teams and avoid unnecessary complexity."
              },
              {
                icon: TrendingUp,
                title: "Business-first delivery.",
                description: "Every engagement is designed around time saved, cost reduced, or revenue protected, not technical novelty."
              },
              {
                icon: ScrollText,
                title: "No inflated promises.",
                description: "If a workflow is not worth automating, we will say so."
              },
              {
                icon: Lock,
                title: "Strong data protection.",
                description: "Robust non-disclosure agreements and confidentiality terms protect your business data and client relationships."
              }
            ].map((promise, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all" data-testid={`promise-${index}`}>
                <div className="w-12 h-12 bg-gradient-to-br from-[#2563EB] to-[#1D4ED8] rounded-xl flex items-center justify-center mb-5">
                  <promise.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-[#0A2540] mb-3 leading-tight">{promise.title}</h3>
                <p className="text-slate-600 text-base leading-relaxed">{promise.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team, hidden per feedback (may be re-enabled) */}
      {SHOW_LEADERSHIP_TEAM && (
        <section className="section-padding">
          <div className="container-custom">
            <h2 className="font-outfit text-3xl font-bold text-[#0A2540] mb-12 text-center">Leadership Team</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {/* Content preserved for future re-enable */}
            </div>
          </div>
        </section>
      )}

      {/* Recognition & Partnerships, hidden per feedback (may be re-enabled) */}
      {SHOW_RECOGNITION && (
        <section className="section-padding bg-[#0A2540]">
          <div className="container-custom text-center">
            <h2 className="font-outfit text-3xl font-bold text-white mb-12">Recognition & Partnerships</h2>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-padding bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
        <div className="container-custom text-center">
          <h2 className="font-outfit text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to Join Our Story?
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto text-lg leading-relaxed">
            Let's discuss how one well-chosen automation or digital initiative can transform your business.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#2563EB] hover:bg-slate-100 rounded-full px-8 py-6 text-lg" data-testid="about-cta-button">
              Book a Discovery Call
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
